import stylex from '@stylexjs/stylex';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { styles } from './styles';

import { Button, Checkbox } from '@shared/ui/kit';
import { EmptyCard } from '@shared/ui/empty-card';
import { OrganizerList } from '../organizer-list';

import { IOrganizer } from '../organizer/model/types';
import { AddButton } from '@shared/ui/add-button';
import { typography } from '@shared/tokens/typography.stylex';
import { anatomy } from '@shared/tokens/anatomy.stylex';

interface OranizerTabProps {
  organizers: IOrganizer[];
  handleDelete: (organizer: IOrganizer) => void;
  handleAddOrganizer: () => void;
  sendTickets: (users: number[]) => void;
  eventId?: number;
}

export const OranizerTab: FC<OranizerTabProps> = (props) => {
  const { organizers, handleDelete, handleAddOrganizer, sendTickets, eventId } = props;

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  const organizerIds = useMemo(() => new Set(organizers.map((org) => org.id!)), [organizers]);

  const handleCheckboxChange = useCallback((id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const checkedOrganizers = useMemo(
    () => organizers.filter((org) => selectedIds.has(org.id!)),
    [organizers, selectedIds]
  );

  useEffect(() => {
    setSelectedIds((prev) => new Set([...prev].filter((id) => organizerIds.has(id))));
  }, [organizerIds]);

  const isAllSelected = useMemo(() => {
    if (!organizerIds.size || selectedIds.size !== organizerIds.size) return false;
    for (const id of organizerIds) {
      if (!selectedIds.has(id)) return false;
    }
    return true;
  }, [organizerIds, selectedIds]);

  const handleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      if (!organizerIds.size) return prev;
      if (prev.size !== organizerIds.size) return new Set(organizerIds);
      for (const id of organizerIds) {
        if (!prev.has(id)) return new Set(organizerIds);
      }
      return new Set();
    });
  }, [organizerIds]);

  const handleSendQrCodes = () => {
    if (!checkedOrganizers.length) return;
    sendTickets(Array.from(selectedIds));
  };

  if (!organizers.length) {
    return <EmptyCard handleAddOrganizer={handleAddOrganizer} />;
  }

  return (
    <>
      <div {...stylex.props(styles.buttonsContainer)}>
        <Button
          onClick={() => handleSendQrCodes()}
          size="small"
          variant="secondary"
          isDisabled={!selectedIds.size}
          settings={{
            height: anatomy['size-24'],
            padding: `0 ${anatomy['space-12']}`,
            fontSize: typography['fontSizeCaption-2'],
            lineHeight: anatomy['size-24'],
            borderRadius: anatomy.borderRadiusXs,
          }}
        >
          Отправить QR-код ({selectedIds.size})
        </Button>
      </div>
      <div {...stylex.props(styles.infoContainer)}>
        <div {...stylex.props(styles.organizersHeader)}>
          <Checkbox isChecked={isAllSelected} onChange={handleSelectAll} />
          <button {...stylex.props(styles.selectAllLabel)} onClick={handleSelectAll}>
            Выбрать всех
          </button>
        </div>
        <div {...stylex.props(styles.accessText)}>Доступ к мероприятию</div>
      </div>
      <OrganizerList
        organizers={organizers}
        onDelete={handleDelete}
        eventId={eventId}
        selectedIds={selectedIds}
        handleCheckboxChange={handleCheckboxChange}
      />
      <AddButton
        onClick={handleAddOrganizer}
        settings={{
          fontSize: '14px',
        }}
      >
        Добавить организатора
      </AddButton>
    </>
  );
};
